/* Purchase Order export — async CSV via the shared reports-tasks pipeline.
 *   POST v1/reports/po/export { poNumbers } -> a PENDING task (returned now).
 *   GET  v1/reports/tasks/{taskId}          -> status (poll until READY/FAILED).
 *   GET  v1/reports/tasks/{taskId}/download -> streams the CSV (GCS).
 * Mirrors outOfStockService — httpSupplier targets REACT_APP_SUPPLIER_API_BASE_URL
 * (…/pharmacy-ims/api/), so paths are v1/reports/…. Envelope: { success, message,
 * data, timestamp }; `unwrap` returns data and throws message on success:false. */
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";
import { saveAs } from "file-saver";

/* Audit header — resolved per call to the signed-in user (defaults to "system"). */
const xUser = () => ({ "X-User": getCurrentUserName() });

/* Async export tasks are tracked under the shared reports-tasks endpoint. */
const TASKS = "v1/reports/tasks";

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Task envelope -> { taskId, status: PENDING | READY | FAILED, raw, fileName, rowCount, message }. */
const normalizeTask = (data = {}) => {
  const raw = (data.status ?? data.state ?? "").toString().toUpperCase();
  let status = "PENDING";
  if (["READY", "COMPLETED", "COMPLETE", "SUCCESS", "DONE"].includes(raw)) status = "READY";
  else if (["FAILED", "FAILURE", "ERROR"].includes(raw)) status = "FAILED";
  return {
    taskId: data.taskId ?? data.id ?? data.reportId ?? null,
    status,
    raw,
    fileName: data.fileName ?? data.filename ?? null,
    rowCount: data.rowCount ?? null,
    message: data.errorMessage ?? data.message ?? null,
  };
};

const poExportService = {
  /* POST v1/reports/po/export { poNumbers } -> normalized PENDING task. */
  requestExport(poNumbers = []) {
    const body = { poNumbers: (poNumbers || []).filter(Boolean) };
    return httpSupplier
      .post("v1/reports/po/export", body, { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId} -> the task, polled until READY / FAILED. */
  exportStatus(taskId) {
    return httpSupplier.get(`${TASKS}/${taskId}`, { headers: xUser() }).then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId}/download -> CSV blob; saves to disk. */
  download(taskId, filename = "purchase-orders.csv") {
    return httpSupplier
      .get(`${TASKS}/${taskId}/download`, { responseType: "blob", headers: xUser() })
      .then((r) => saveAs(new Blob([r.data], { type: "text/csv;charset=utf-8" }), filename));
  },
};

export default poExportService;
